import type { FloraGrowthDefinition, GrowthCurve, DensityCurve } from './FloraDefinition';

/** Resolved, non-uniform size of a plant at a given maturity. All lengths in meters. */
export interface GrowthState {
  heightMeters: number;
  spreadMeters: number;
  /** Trunk/base radius in meters. */
  trunkRadiusMeters: number;
  /** Height of the canopy underside above ground (trees lift their canopy as they mature). */
  canopyBaseMeters: number;
  /** Foliage density in [0, 1]. */
  foliageDensity: number;
}

/** Optional per-instance biological modifiers (spec §11) that scale the procedural model. */
export interface GrowthModifiers {
  /** Overall size multiplier offset in [-0.5, 0.5] (0 = nominal). */
  sizeVariation?: number;
  /** Additional height multiplier offset. */
  heightVariation?: number;
  /** Additional spread multiplier offset. */
  spreadVariation?: number;
  /** Foliage fullness multiplier in [0, 1+]. */
  fullness?: number;
  /** Health in [0, 1] — reduces density when low. */
  health?: number;
}

function clamp01(t: number): number {
  return Math.min(Math.max(t, 0), 1);
}

/** Growth easing curves (spec §8.3). Input/output in [0, 1]. */
export function applyGrowthCurve(t: number, curve: GrowthCurve): number {
  const x = clamp01(t);
  switch (curve) {
    case 'linear':
      return x;
    case 'ease-out':
      return 1 - (1 - x) * (1 - x);
    case 'logistic': {
      // Normalized logistic centered at 0.5 so f(0)=0 and f(1)=1.
      const k = 10;
      const raw = (v: number): number => 1 / (1 + Math.exp(-k * (v - 0.5)));
      const f0 = raw(0);
      const f1 = raw(1);
      return (raw(x) - f0) / (f1 - f0);
    }
  }
}

/** Density curves for foliage (spec §8.3). */
export function applyDensityCurve(t: number, curve: DensityCurve): number {
  const x = clamp01(t);
  switch (curve) {
    case 'linear':
      return x;
    case 'ease-in':
      return x * x;
    case 'ease-out':
      return 1 - (1 - x) * (1 - x);
  }
}

/** Smallest supported nursery height/spread (maturity 0) from the container stages. */
function smallestStage(def: FloraGrowthDefinition): { height: number; spread: number } {
  let height = Number.POSITIVE_INFINITY;
  let spread = Number.POSITIVE_INFINITY;
  for (const stage of def.containerStages) {
    if (stage.heightMeters < height) height = stage.heightMeters;
    if (stage.spreadMeters < spread) spread = stage.spreadMeters;
  }
  if (!Number.isFinite(height)) height = 0.2;
  if (!Number.isFinite(spread)) spread = 0.2;
  return { height, spread };
}

/**
 * Compute the non-uniform {@link GrowthState} for a plant at a given maturity. Height, spread,
 * trunk radius, canopy base, and foliage density each follow their own curve/rate so a maturity
 * change is never a single uniform scale (spec §8.3). Maturity may exceed 1 to extrapolate toward
 * the maximum mature dimensions.
 */
export function computeGrowthState(
  def: FloraGrowthDefinition,
  maturity: number,
  modifiers: GrowthModifiers = {},
): GrowthState {
  const m = Math.max(0, maturity);
  const base = smallestStage(def);

  const heightTypical = def.matureHeightMeters.typical;
  const spreadTypical = def.matureSpreadMeters.typical;

  const hCurve = applyGrowthCurve(Math.min(m, 1), def.heightCurve);
  const sCurve = applyGrowthCurve(Math.min(m, 1), def.spreadCurve);

  let heightMeters = base.height + (heightTypical - base.height) * hCurve;
  let spreadMeters = base.spread + (spreadTypical - base.spread) * sCurve;

  // Extrapolate beyond typical toward maximum for exceptional specimens.
  if (m > 1) {
    const extra = m - 1;
    heightMeters += (def.matureHeightMeters.maximum - heightTypical) * extra;
    spreadMeters += (def.matureSpreadMeters.maximum - spreadTypical) * extra;
  }

  // Apply size modifiers (multiplicative offsets around 1).
  const sizeMul = 1 + (modifiers.sizeVariation ?? 0);
  heightMeters *= sizeMul * (1 + (modifiers.heightVariation ?? 0));
  spreadMeters *= sizeMul * (1 + (modifiers.spreadVariation ?? 0));

  // Trunk radius: allometric grows faster with height than linear.
  const trunkCurve = def.trunkCurve ?? 'linear';
  const trunkRadiusMeters =
    trunkCurve === 'allometric'
      ? 0.02 + Math.pow(heightMeters, 1.3) * 0.012
      : 0.02 + heightMeters * 0.02;

  // Canopy base rises with maturity (only meaningfully for taller/tree forms).
  const canopyBaseMeters = heightMeters * 0.25 * applyGrowthCurve(Math.min(m, 1), 'ease-out');

  // Foliage density from density curve, scaled by fullness and health.
  const densityCurve = def.densityCurve ?? 'linear';
  const health = modifiers.health ?? 1;
  const fullness = modifiers.fullness ?? 1;
  const foliageDensity = clamp01(
    (0.35 + 0.65 * applyDensityCurve(Math.min(m, 1), densityCurve)) * fullness * health,
  );

  return { heightMeters, spreadMeters, trunkRadiusMeters, canopyBaseMeters, foliageDensity };
}
